document.addEventListener("turbolinks:load", boot);
document.addEventListener("turbolinks:before-cache", teardown);

function boot() {
  const form = document.getElementById("planAmendmentForm");
  if (!form) return;

  if (form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  const planPageCount = parseInt(form.dataset.planPageCount || "1", 10);
  const isSection = form.dataset.isSection === "true";
  const sectionMaxPages = parseInt(form.dataset.sectionMaxPages || "0", 10);

  function projectedPageCount(start, place, incoming, currentCount) {
    if (!incoming || incoming <= 0) return null;
    if (place === "before" || place === "after") return currentCount + incoming;
    if (place === "replace") {
      const pagesReplaced = Math.min(incoming, currentCount - start + 1);
      return currentCount - pagesReplaced + incoming;
    }
    return currentCount;
  }

  function wouldExceedSectionLimit(projected) {
    return isSection && sectionMaxPages > 0 && projected != null && projected > sectionMaxPages;
  }

  const pagesInput       = document.getElementById("addStartPage") || document.getElementById("plan_amendment_pages");
  const fileInput        = document.getElementById("plan_amendment_file");
  const hintEl           = document.getElementById("addHint");
  const appendBtn        = document.getElementById("addAppendToEndBtn");
  const placementSelect  = document.getElementById("placementSelect");
  const submitBtn        = document.getElementById("planAmendmentSubmitBtn");
  const replaceWarning   = document.getElementById("replaceWarning");
  const replaceWarningText = document.getElementById("replaceWarningText");
  const sectionLimitAlert = document.getElementById("sectionPageLimitAlert");
  const sectionLimitAlertText = document.getElementById("sectionPageLimitAlertText");

  let uploadedPages = 0;

  function currentProjectedPageCount(start, place) {
    if (!uploadedPages || uploadedPages <= 0 || !start || start < 1) return null;
    return projectedPageCount(start, place, uploadedPages, planPageCount);
  }

  function syncSectionPageLimitAlert(start, place, scrollIntoView) {
    if (!isSection || !sectionLimitAlert) return false;

    const projected = currentProjectedPageCount(start, place);
    const over = wouldExceedSectionLimit(projected);

    if (over && projected != null) {
      sectionLimitAlert.style.display = "block";
      if (sectionLimitAlertText) {
        sectionLimitAlertText.textContent =
          `This operation would result in ${projected} pages. Sections cannot have more than ${sectionMaxPages} pages. Choose a smaller PDF or re-section this plan.`;
      }
      if (submitBtn) submitBtn.disabled = true;
      if (scrollIntoView) {
        sectionLimitAlert.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      sectionLimitAlert.style.display = "none";
      if (submitBtn && form.dataset.submitting !== "1") submitBtn.disabled = false;
    }

    return over;
  }

  function currentPlacement() {
    return placementSelect ? placementSelect.value : "before";
  }

  function updateHint() {
    if (!hintEl) return;
    const start = parseInt(pagesInput?.value || "0", 10);
    const place = currentPlacement();

    if (!start || start < 1) {
      hintEl.textContent = `Range: 1–${planPageCount}. Choose a starting page.`;
      if (replaceWarning) replaceWarning.style.display = "none";
      syncSectionPageLimitAlert(start, place);
      return;
    }

    if (place === "replace") {
      if (uploadedPages > 0) {
        const end = Math.min(start + uploadedPages - 1, planPageCount);
        const pagesReplaced = end - start + 1;
        const finalPageCount = planPageCount - pagesReplaced + uploadedPages;
        
        hintEl.textContent = `Will replace pages ${start}–${end} (${uploadedPages} page${uploadedPages > 1 ? "s" : ""}).` +
          (isSection ? ` Final count: ${finalPageCount} pages.` : "");
        
        // Show warning for multi-page replacement
        if (replaceWarning && replaceWarningText && uploadedPages > 1) {
          replaceWarningText.textContent = `Replacing page ${start} with ${uploadedPages} pages will delete pages ${start}–${end} and insert ${uploadedPages} new pages. Final document will have ${finalPageCount} pages.`;
          replaceWarning.style.display = "block";
        } else if (replaceWarning) {
          replaceWarning.style.display = "none";
        }
      } else {
        hintEl.textContent = `Will replace a range starting at page ${start} equal to the uploaded PDF length.`;
        if (replaceWarning) replaceWarning.style.display = "none";
      }
    } else {
      // Insert before/after
      if (replaceWarning) replaceWarning.style.display = "none";
      
      if (place === "before") {
        hintEl.textContent = uploadedPages > 0
          ? `Will insert ${uploadedPages} page${uploadedPages > 1 ? "s" : ""} before page ${start}.` +
            (isSection ? ` Final count: ${planPageCount + uploadedPages} pages.` : "")
          : `Will insert your uploaded pages before page ${start}.`;
      } else {
        // after
        if (start > planPageCount) {
          // Appending to the end
          hintEl.textContent = uploadedPages > 0
            ? `Will append ${uploadedPages} page${uploadedPages > 1 ? "s" : ""} to the end of the document.` +
              (isSection ? ` Final count: ${planPageCount + uploadedPages} pages.` : "")
            : `Will append your uploaded pages to the end of the document.`;
        } else {
          hintEl.textContent = uploadedPages > 0
            ? `Will insert ${uploadedPages} page${uploadedPages > 1 ? "s" : ""} after page ${start}.` +
              (isSection ? ` Final count: ${planPageCount + uploadedPages} pages.` : "")
            : `Will insert your uploaded pages after page ${start}.`;
        }
      }
    }

    syncSectionPageLimitAlert(start, place);
  }

  // Detect uploaded PDF page count (uses PDF.js if present; regex fallback)
  async function detectPdfPages(file) {
    try {
      if (window.pdfjsLib && window.pdfjsLib.getDocument) {
        const data = await file.arrayBuffer();
        const pdf = await window.pdfjsLib.getDocument({ data }).promise;
        return pdf.numPages || 0;
      }
    } catch (_) { /* ignore and fall back */ }

    try {
      const text = await file.text();
      const matches = text.match(/\/Type\s*\/Page\b/g);
      return matches ? matches.length : 0;
    } catch {
      return 0;
    }
  }

  async function onFileChanged() {
    const file = fileInput?.files?.[0];
    uploadedPages = file ? (await detectPdfPages(file)) : 0;
    updateHint();
  }

  function onAppendToEnd() {
    if (!pagesInput) return;
    pagesInput.value = String(planPageCount);
    if (placementSelect) placementSelect.value = "after";
    updateHint();
  }

  function confirmAndSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation();

    // avoid duplicate submits
    if (form.dataset.submitting === "1") return;

    const start = parseInt(pagesInput?.value || "0", 10);
    const place = currentPlacement();
    const maxAllowedPage = currentPlacement() === "after" ? planPageCount + 1 : planPageCount;
    
    if (!start || start < 1 || start > maxAllowedPage) {
      alert(`Please enter a valid page number between 1 and ${maxAllowedPage}`);
      return;
    }
    
    if (!fileInput?.files?.length) {
      alert("Please select a PDF file to upload");
      return;
    }

    if (syncSectionPageLimitAlert(start, place, true)) {
      return;
    }

    let msg = "";
    if (place === "replace") {
      if (uploadedPages > 0) {
        const end  = Math.min(start + uploadedPages - 1, planPageCount);
        const unit = uploadedPages === 1 ? "page" : "pages";
        msg = uploadedPages === 1
          ? `Confirm replace page ${start} with your upload (1 page)?`
          : `Confirm replace pages ${start}–${end} with your upload (${uploadedPages} ${unit})?`;
      } else {
        msg = `We couldn't determine the uploaded PDF length yet. The server will replace a range starting at page ${start} equal to the uploaded length. Continue?`;
      }
    } else if (place === "before") {
      const unit = uploadedPages === 1 ? "page" : "pages";
      msg = uploadedPages > 0
        ? `Confirm insert ${uploadedPages} ${unit} before page ${start}?`
        : `Confirm insert your uploaded pages before page ${start}?`;
    } else {
      const unit = uploadedPages === 1 ? "page" : "pages";
      if (start > planPageCount) {
        // Appending to the end
        msg = uploadedPages > 0
          ? `Confirm append ${uploadedPages} ${unit} to the end of the document?`
          : `Confirm append your uploaded pages to the end of the document?`;
      } else {
        msg = uploadedPages > 0
          ? `Confirm insert ${uploadedPages} ${unit} after page ${start}?`
          : `Confirm insert your uploaded pages after page ${start}?`;
      }
    }

    if (!window.confirm(msg)) return;

    form.dataset.submitting = "1";
    if (submitBtn) submitBtn.disabled = true;

    HTMLFormElement.prototype.submit.call(form);
  }

  // Pressing Enter (outside textareas) acts like clicking Save
  function onFormKeydown(ev) {
    if (ev.key === "Enter" && ev.target.tagName !== "TEXTAREA") {
      confirmAndSubmit(ev);
    }
  }

  // Wire events (once)
  pagesInput      && pagesInput.addEventListener("input",  updateHint);
  fileInput       && fileInput.addEventListener("change", onFileChanged);
  placementSelect && placementSelect.addEventListener("change", updateHint);
  appendBtn       && appendBtn.addEventListener("click", onAppendToEnd);
  
  if (submitBtn) {
    submitBtn.addEventListener("click", confirmAndSubmit);
    form.addEventListener("submit", function(e) {
      e.preventDefault();
      confirmAndSubmit(e);
    });
  }
  
  form.addEventListener("keydown", onFormKeydown);

  // Save refs for teardown
  form._pm_onClickSubmit = confirmAndSubmit;
  form._pm_onKeydown     = onFormKeydown;

  // Initial hint
  updateHint();
}

function teardown() {
  const form = document.getElementById("planAmendmentForm");
  if (!form) return;

  const submitBtn = document.getElementById("planAmendmentSubmitBtn");

  if (form._pm_onClickSubmit && submitBtn) {
    submitBtn.removeEventListener("click", form._pm_onClickSubmit);
    delete form._pm_onClickSubmit;
  }
  if (form._pm_onKeydown) {
    form.removeEventListener("keydown", form._pm_onKeydown);
    delete form._pm_onKeydown;
  }

  form.dataset.bound = "0";
}
